/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type ModelDetailsObject = {
    name: string;
    f1Score: number;
    expiryDate: Date;
    isLoading?: boolean;
    hasSnapshot: boolean;
    isClickable?: boolean;
    entityF1Score?: number;
    testingUtterances?: number;
    trainingUtterances?: number;
};

export type ModelDetailsListTableProps = TableProps<ModelDetailsObject> & {
    f1ScoreHeaderName?: string;
    showEvaluationColumns?: boolean;
    hiddenColumnKeys?: string[];
    isModelLinkAttributeName: string;
    onModelClick?: (modelName: string) => void;
};

const messages = defineMessages({
    status: { id: 'modelDetailsListTable.status', defaultMessage: 'Status' },
    f1Score: { id: 'modelDetailsListTable.f1Score', defaultMessage: 'F1 score' },
    training: { id: 'modelDetailsListTable.training', defaultMessage: 'Training' },
    modelName: { id: 'modelDetailsListTable.modelName', defaultMessage: 'Model name' },
    expiryDate: { id: 'modelDetailsListTable.expiryDate', defaultMessage: 'Model expiry date' },
    entityF1Score: { id: 'modelDetailsListTable.entityF1Score', defaultMessage: 'Entity F1 score' },
    modelExpired: { id: 'modelDetailsListTable.modelExpired', defaultMessage: 'Retrain your model' },
    trainingFailed: { id: 'modelDetailsListTable.trainingFailed', defaultMessage: 'Training failed' },
    trainingStaredOn: { id: 'modelDetailsListTable.trainingStaredOn', defaultMessage: 'Training started on' },
    testingUtterances: { id: 'modelDetailsListTable.testingUtterances', defaultMessage: 'Testing Utterances' },
    trainingSucceeded: { id: 'modelDetailsListTable.trainingSucceeded', defaultMessage: 'Training succeeded' },
    trainingUtterances: { id: 'modelDetailsListTable.trainingUtterances', defaultMessage: 'Training Utterances' },
    trainingNotStarted: { id: 'modelDetailsListTable.trainingNotStarted', defaultMessage: 'Training not started' },
    modelAboutToExpire: { id: 'modelDetailsListTable.modelAboutToExpire', defaultMessage: 'Model about to expire' },
    emptyTablePlaceholder: { id: 'modelDetailsListTable.emptyTablePlaceholder', defaultMessage: 'No models found.' },
    trainingCompletedOn: { id: 'modelDetailsListTable.trainingCompletedOn', defaultMessage: 'Training completed on' }
});

const theme = getScopedTheme('ModelDetailsListTable');
const classNames = mergeStyleSets({
    fileIconHeaderIcon: { padding: 0, fontSize: '16px' },
    fileIconCell: {
        textAlign: 'center',
        selectors: {
            '&:before': {
                content: '.',
                width: '0px',
                height: '100%',
                visibility: 'hidden',
                display: 'inline-block',
                verticalAlign: 'middle'
            }
        }
    }
});

const iconStyles = (color: string) =>
    classNamesFunction<IIconStyleProps, IIconStyles>()({
        root: { color, marginLeft: theme.expiryIconLeftMargin, marginRight: theme.expiryIconRightMargin }
    });

export const ModelDetailsListTable = (props: ModelDetailsListTableProps) => {
    const { localize } = useLocalization();
    const {
        items,
        sortKey,
        onModelClick,
        selectionMode,
        hiddenColumnKeys = [],
        isModelLinkAttributeName,
        showEvaluationColumns = true,
        f1ScoreHeaderName = localize(messages.f1Score),
        ...rest
    } = props;

    const onRenderName = (item: ModelDetailsObject) => {
        if (item.isLoading) {
            return <Spinner size={SpinnerSize.small} />;
        }

        return (
            <NameCellLink
                {...{ [isModelLinkAttributeName]: item.isClickable ? 'true' : 'false' }}
                onClick={() => onModelClick(item.name)}
                disabled={!item.isClickable}>
                {item.name}
            </NameCellLink>
        );
    };

    const onRenderF1Score = (score: number) => (
        <LuisText title={`${score}`}>{score === 0 || score === 1 ? score : score?.toFixed(3) ?? '-'}</LuisText>
    );

    const onRenderUtterancesNumber = (item: ModelDetailsObject, utteranceType: 'testing' | 'training') =>
        utteranceType === 'testing' ? item.testingUtterances : item.trainingUtterances;

    const onRenderExpiryDate = (item: ModelDetailsObject) => {
        if (!item?.expiryDate) {
            return <LuisText>--</LuisText>;
        }

        const monthBeforeExpiry = item.expiryDate.getTime() - 30 * 24 * 60 * 60 * 1000;

        if (item.expiryDate.getTime() < new Date().getTime()) {
            return (
                <>
                    <LuisText>{item.expiryDate.toLocaleDateString()}</LuisText>
                    <Icon iconName="ErrorBadge" styles={iconStyles(theme.errorIconColor)} />
                    <ExternalLink href="https://aka.ms/custom-model-expiry">{localize(messages.modelExpired)}</ExternalLink>
                </>
            );
        }

        if (monthBeforeExpiry < new Date().getTime()) {
            return (
                <>
                    <LuisText>{item.expiryDate.toLocaleDateString()}</LuisText>
                    <Icon iconName="Warning12" styles={iconStyles(theme.warningIconColor)} />
                    <ExternalLink href="https://aka.ms/custom-model-expiry">{localize(messages.modelAboutToExpire)}</ExternalLink>
                </>
            );
        }

        return <LuisText>{item.expiryDate.toLocaleDateString()}</LuisText>;
    };

    const columns: IColumn[] = [
        {
            minWidth: 16,
            maxWidth: 16,
            key: 'fileIcon',
            iconName: 'Page',
            isIconOnly: true,
            name: 'File icon',
            className: classNames.fileIconCell,
            iconClassName: classNames.fileIconHeaderIcon
        },
        {
            key: 'name',
            minWidth: 300,
            maxWidth: 500,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            onRender: onRenderName,
            isSorted: sortKey?.key === 'name',
            name: localize(messages.modelName),
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc'
        },

        ...(showEvaluationColumns
            ? [
                  {
                      key: 'f1Score',
                      minWidth: 100,
                      maxWidth: 200,
                      isResizable: true,
                      fieldName: 'f1Score',
                      name: f1ScoreHeaderName,
                      isSorted: sortKey?.key === 'f1Score',
                      onRender: (item: ModelDetailsObject) => onRenderF1Score(item.f1Score),
                      isSortedDescending: sortKey?.key === 'f1Score' && sortKey?.order === 'desc'
                  },
                  {
                      minWidth: 100,
                      maxWidth: 200,
                      isResizable: true,
                      key: 'entityF1Score',
                      fieldName: 'entityF1Score',
                      name: localize(messages.entityF1Score),
                      isSorted: sortKey?.key === 'entityF1Score',
                      onRender: (item: ModelDetailsObject) => onRenderF1Score(item.entityF1Score),
                      isSortedDescending: sortKey?.key === 'entityF1Score' && sortKey?.order === 'desc'
                  },
                  {
                      minWidth: 100,
                      maxWidth: 200,
                      isResizable: true,
                      key: 'trainingUtterances',
                      fieldName: 'trainingUtterances',
                      name: localize(messages.trainingUtterances),
                      isSorted: sortKey?.key === 'trainingUtterances',
                      onRender: (item: ModelDetailsObject) => onRenderUtterancesNumber(item, 'training'),
                      isSortedDescending: sortKey?.key === 'trainingUtterances' && sortKey?.order === 'desc'
                  },
                  {
                      minWidth: 100,
                      maxWidth: 200,
                      isResizable: true,
                      key: 'testingUtterances',
                      fieldName: 'testingUtterances',
                      name: localize(messages.testingUtterances),
                      isSorted: sortKey?.key === 'testingUtterances',
                      onRender: (item: ModelDetailsObject) => onRenderUtterancesNumber(item, 'testing'),
                      isSortedDescending: sortKey?.key === 'testingUtterances' && sortKey?.order === 'desc'
                  }
              ]
            : []),
        {
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            key: 'expiryDate',
            isRowHeader: false,
            fieldName: 'expiryDate',
            onRender: onRenderExpiryDate,
            name: localize(messages.expiryDate),
            isSorted: sortKey?.key === 'expiryDate',
            isSortedDescending: sortKey?.key === 'expiryDate' && sortKey?.order === 'desc'
        }
    ].filter(Boolean);

    return (
        <Table
            items={items}
            selectionMode={selectionMode}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns.filter(c => !hiddenColumnKeys.includes(c.key))}
            {...rest}
        />
    );
};
